import { isPublicId } from '@stockstop/shared';
import { useCallback, useEffect, useState } from 'react';

/**
 * Welcher Wert gerade ausgewählt ist — im **Pfad**, nicht im Fragezeichen-Teil.
 *
 * Gebraucht wird das für die Links aus der täglichen Auswertung: Eine Nachricht,
 * die drei Werte nennt und auf die Startseite verweist, zwingt zum Suchen.
 *
 * **`/stock/<Kennung>`, und die Kennung ist zufällig.** Nicht die laufende `id`:
 * Eine `4` in einer Adresse verrät, dass es eine 3 und eine 5 gibt, und lädt zum
 * Durchprobieren ein. Auch nicht das Tickersymbol — das wäre lesbar und verriete
 * damit genau das Schützenswerte, nämlich welches Papier jemand hält.
 *
 * **Die Kennung ist trotzdem kein Geheimnis.** Adressen landen im Browserverlauf,
 * im Mailprogramm und in Protokollen. Zugriff gibt allein der Mandantenfilter am
 * Server; die Kennung sorgt nur dafür, dass eine durchgesickerte Adresse nichts
 * über das Depot aussagt.
 *
 * **Kein Router.** Die Anwendung hat genau einen Zustand, der adressierbar sein
 * muss; eine Routingbibliothek dafür wäre eine Abhängigkeit für eine Zeichenkette.
 *
 * **`replaceState`, nicht `pushState`.** Die Auswahl ist ein Blick auf dieselbe
 * Ansicht, kein Seitenwechsel. Sonst sammelte jeder Klick in der Liste einen
 * Verlaufseintrag, und wer die Anwendung über „Zurück" verlassen will, müsste ein
 * Dutzend Mal drücken.
 */

const PRAEFIX = '/stock/';

/**
 * Zwischenlager für den Weg durch die Anmeldung.
 *
 * Google leitet nach der Anmeldung auf `APP_BASE_URL` zurück — auf die Wurzel,
 * ohne den Pfad. Ein Link aus der E-Mail, der einen abgemeldeten Browser trifft,
 * verlöre sein Ziel also genau dann, wenn er gebraucht wird: nachts gelesen,
 * morgens angeklickt, Sitzung abgelaufen.
 *
 * `sessionStorage` und nicht `localStorage`: Das Ziel gilt für diesen einen
 * Anlauf in diesem einen Tab, nicht für den Browser auf Dauer.
 */
const SCHLUESSEL = 'stockstop.ziel-wert';

/** Die Kennung aus dem Pfad, sofern einer darin steht. */
const ausPfad = (): string | null => {
  try {
    const pfad = window.location.pathname;
    if (!pfad.startsWith(PRAEFIX)) return null;
    const roh = decodeURIComponent(pfad.slice(PRAEFIX.length)).replace(/\/+$/, '');
    return isPublicId(roh) ? roh : null;
  } catch {
    /* Ein kaputt maskierter Pfad ist kein Grund, die Anwendung nicht zu zeigen. */
    return null;
  }
};

/* Jeder Speicherzugriff abgesichert: Im privaten Fenster wirft schon das Lesen,
 * und eine Anwendung, die daran scheitert, wäre wegen einer Randnotiz nicht mehr
 * bedienbar. Dasselbe Vorgehen wie bei der Seitenleiste. */
const merken = (publicId: string): void => {
  try {
    window.sessionStorage.setItem(SCHLUESSEL, publicId);
  } catch {
    /* Dann klappt der Weg durch die Anmeldung nicht — die Anwendung schon. */
  }
};

const gemerktesZiel = (): string | null => {
  try {
    const roh = window.sessionStorage.getItem(SCHLUESSEL);
    if (roh === null) return null;
    window.sessionStorage.removeItem(SCHLUESSEL);
    return isPublicId(roh) ? roh : null;
  } catch {
    return null;
  }
};

/** Schreibt die Auswahl in den Pfad, ohne den Verlauf zu füllen. */
const inPfad = (publicId: string | null): void => {
  try {
    const ziel = publicId === null ? '/' : `${PRAEFIX}${encodeURIComponent(publicId)}`;
    if (window.location.pathname !== ziel) {
      window.history.replaceState(null, '', `${ziel}${window.location.search}`);
    }
  } catch {
    /* Ohne Adresszeile bleibt die Auswahl trotzdem bedienbar. */
  }
};

interface Auswaehlbar {
  id: number;
  publicId: string | null;
}

export interface SelectedPosition {
  selectedId: number | null;
  select: (id: number | null) => void;
}

export const useSelectedPosition = (positions: readonly Auswaehlbar[]): SelectedPosition => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  /*
   * Das Ziel aus dem Pfad, bis das Portfolio da ist.
   *
   * Beim ersten Aufbau ist die Liste leer, die Kennung lässt sich also noch nicht
   * auf eine Position abbilden. Sie wird deshalb festgehalten und aufgelöst,
   * sobald die Daten eintreffen — sonst stünde die Auswahl auf dem ersten
   * Eintrag, bevor der Link überhaupt zum Zug käme.
   *
   * Die Adresse gewinnt vor dem Zwischenlager: Ein frischer Link soll ein altes
   * gemerktes Ziel überschreiben, nicht umgekehrt.
   */
  const [zielPublicId, setZielPublicId] = useState<string | null>(
    () => ausPfad() ?? gemerktesZiel(),
  );

  /* Das Ziel über die Anmeldung retten, bevor Google den Tab übernimmt. */
  useEffect(() => {
    const ziel = ausPfad();
    if (ziel !== null) merken(ziel);
  }, []);

  /* Vor und Zurück im Browser sollen die Auswahl mitnehmen. */
  useEffect(() => {
    const beiSprung = (): void => setZielPublicId(ausPfad());
    window.addEventListener('popstate', beiSprung);
    return () => window.removeEventListener('popstate', beiSprung);
  }, []);

  /*
   * Auswahl bestimmen: erst das Ziel aus der Adresse, sonst der erste Eintrag.
   *
   * Zeigt ein Link auf einen Wert, den es nicht mehr gibt — verkauft, gelöscht,
   * oder aus einem fremden Depot —, fällt die Auswahl auf den ersten Eintrag
   * zurück. Eine Fehlermeldung wäre hier die schlechtere Antwort: Der Nutzer
   * wollte sein Portfolio sehen, und das kann er.
   */
  useEffect(() => {
    if (positions.length === 0) {
      setSelectedId(null);
      return;
    }

    if (zielPublicId !== null) {
      const gemeint = positions.find((position) => position.publicId === zielPublicId);
      setZielPublicId(null);
      if (gemeint) {
        setSelectedId(gemeint.id);
        return;
      }
    }

    if (selectedId === null || !positions.some((position) => position.id === selectedId)) {
      setSelectedId(positions[0]!.id);
    }
  }, [positions, selectedId, zielPublicId]);

  /* Den Pfad nachführen — erst, wenn die Auswahl aufgelöst ist. */
  useEffect(() => {
    if (zielPublicId !== null) return;
    const gewaehlt = positions.find((position) => position.id === selectedId);
    inPfad(gewaehlt?.publicId ?? null);
  }, [selectedId, positions, zielPublicId]);

  const select = useCallback((id: number | null) => setSelectedId(id), []);

  return { selectedId, select };
};
